import { fieldTeamMemberFromIdToken } from "../../../../util/fieldTeamMemberFromIdToken";

const browserLocalStorage = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
};

type WifiScanRow = {
  ssid: string;
  signalStrength: number;
  secured: boolean;
  /** From device CGI `ap.json` when available (0=open, 3=WPA2, 8=WPA3). */
  auth?: number;
};

function normalizeWifiScanResult(raw: unknown): WifiScanRow[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    const out: WifiScanRow[] = [];
    for (const item of raw) {
      if (typeof item === "string") {
        if (item.length > 0) {
          out.push({ ssid: item, signalStrength: 0, secured: true });
        }
        continue;
      }
      if (typeof item === "object" && item !== null && "ssid" in item) {
        const o = item as Record<string, unknown>;
        const ssid = String(o.ssid ?? "");
        if (!ssid) continue;
        const auth =
          typeof o.auth === "number" && Number.isFinite(o.auth)
            ? o.auth
            : undefined;
        const signalStrength =
          typeof o.rssi === "number"
            ? o.rssi
            : typeof o.signalStrength === "number"
              ? o.signalStrength
              : 0;
        const secured =
          typeof o.secured === "boolean"
            ? o.secured
            : auth !== undefined
              ? auth !== 0
              : true;
        out.push({
          ssid,
          signalStrength,
          secured,
          ...(auth !== undefined ? { auth } : {}),
        });
      }
    }
    return out;
  }

  if (typeof raw === "object" && raw !== null && "networks" in raw) {
    const nets = (raw as { networks: unknown }).networks;
    if (!Array.isArray(nets)) return [];
    return normalizeWifiScanResult(nets);
  }

  return [];
}

function mapNativeProvisionResult(
  result: unknown
): Awaited<ReturnType<ICircadiaEngine["provisionDeviceWifi"]>> {
  if (result && typeof result === "object" && "success" in result) {
    return (result as { success: boolean }).success
      ? { status: "connected" }
      : { status: "timeout" };
  }
  if (
    result &&
    typeof result === "object" &&
    "status" in result &&
    typeof (result as { status: unknown }).status === "string"
  ) {
    return result as Awaited<ReturnType<ICircadiaEngine["provisionDeviceWifi"]>>;
  }
  return { status: "timeout" };
}

type FieldTeamLoginResult = Awaited<
  ReturnType<ICircadiaEngine["fieldTeamLogin"]>
>;

/** Resolved shape from `cognitoUserLogin` / `cognitoProvideNewPassword` (Capacitor). */
type NativeCognitoLoginResult = {
  userId?: string;
  email?: string;
  challengePending?: boolean;
};

const emptyFieldTeamMember = (): FieldTeamLoginResult["member"] => ({
  id: "",
  name: "",
  email: "",
  role: "field_technician",
});

const failFieldTeamLogin = (): FieldTeamLoginResult => ({
  success: false,
  member: emptyFieldTeamMember(),
  token: "",
});

async function awaitIfPromise<T>(raw: unknown): Promise<T | undefined> {
  if (
    raw &&
    typeof raw === "object" &&
    "then" in raw &&
    typeof (raw as Promise<T>).then === "function"
  ) {
    return await (raw as Promise<T>);
  }
  return undefined;
}

async function finalizeNativeFieldTeamLogin(
  c: ICircadiaEngine & {
    cognitoTokens?: () => Promise<{ AccessToken?: string; IdToken?: string }>;
  },
  email: string
): Promise<FieldTeamLoginResult> {
  try {
    const tokens = await c.cognitoTokens?.();
    const idToken = tokens?.IdToken ?? "";
    const member = idToken
      ? fieldTeamMemberFromIdToken(idToken, email)
      : null;
    if (member && idToken) {
      return { success: true, member, token: idToken };
    }
    return failFieldTeamLogin();
  } catch {
    return failFieldTeamLogin();
  }
}

/**
 * Native Cognito login: `cognitoUserLogin` resolves with `{ challengePending }` on
 * challenge, while `cognitoLoginDone` only fires after tokens exist. Older code
 * ignored the promise and waited only for `cognitoLoginDone`, so NEW_PASSWORD /
 * MFA never completed and the UI spinner ran forever.
 */
function fieldTeamLoginViaNativeCognitoEvents(
  circadia: ICircadiaEngine,
  email: string,
  password: string
): Promise<FieldTeamLoginResult> {
  const c = circadia as ICircadiaEngine & {
    setCognitoLoginDoneCb?: (cb: (() => void) | null) => Promise<unknown>;
    setCognitoLoginFailedCb?: (cb: ((reason: unknown) => void) | null) =>
      | Promise<unknown>
      | unknown;
    setCognitoNewPasswordRequiredCb?: (
      cb: ((params: { requiredAttributes?: string[] }) => void) | null
    ) => Promise<unknown>;
    setCognitoMfaRequiredCb?: (
      cb: ((params: Record<string, unknown>) => void) | null
    ) => Promise<unknown>;
    setCognitoEmailOTPRequiredCb?: (cb: (() => void) | null) => Promise<unknown>;
    setCognitoPinRequiredCb?: (cb: (() => void) | null) => Promise<unknown>;
    setCognitoCustomChallengeRequiredCb?: (
      cb: ((params: { challengeName?: string }) => void) | null
    ) => Promise<unknown>;
    cognitoUserLogin?: (
      u: string,
      p: string
    ) => void | Promise<NativeCognitoLoginResult>;
    cognitoTokens?: () => Promise<{ AccessToken?: string; IdToken?: string }>;
  };

  if (typeof c.cognitoUserLogin !== "function") {
    return Promise.resolve(failFieldTeamLogin());
  }

  return new Promise((resolve) => {
    let settled = false;

    const cleanup = async () => {
      await Promise.resolve(c.setCognitoLoginDoneCb?.(null));
      await Promise.resolve(c.setCognitoLoginFailedCb?.(null));
      await Promise.resolve(c.setCognitoNewPasswordRequiredCb?.(null));
      await Promise.resolve(c.setCognitoMfaRequiredCb?.(null));
      await Promise.resolve(c.setCognitoEmailOTPRequiredCb?.(null));
      await Promise.resolve(c.setCognitoPinRequiredCb?.(null));
      await Promise.resolve(c.setCognitoCustomChallengeRequiredCb?.(null));
    };

    const settle = (out: FieldTeamLoginResult) => {
      if (settled) return;
      settled = true;
      void cleanup().then(() => resolve(out));
    };

    const finishWithTokens = async () => {
      if (settled) return;
      const out = await finalizeNativeFieldTeamLogin(c, email);
      settle(out);
    };

    void (async () => {
      await c.setCognitoLoginDoneCb?.(() => {
        void finishWithTokens();
      });
      await c.setCognitoLoginFailedCb?.(() => {
        settle(failFieldTeamLogin());
      });

      await c.setCognitoNewPasswordRequiredCb?.((params) => {
        settle({
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "NEW_PASSWORD_REQUIRED",
          requiredUserAttributes: params?.requiredAttributes ?? [],
        });
      });

      await c.setCognitoMfaRequiredCb?.((params) => {
        const p = params as {
          type?: string;
          delivery?: { destination?: string };
          CODE_DELIVERY_DESTINATION?: string;
        };
        const kind: FieldTeamLoginResult["challenge"] =
          p?.type === "SOFTWARE_TOKEN_MFA"
            ? "SOFTWARE_TOKEN_MFA"
            : "SMS_MFA";
        const dest =
          p?.delivery?.destination ?? p?.CODE_DELIVERY_DESTINATION;
        settle({
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: kind,
          mfaDestination: dest,
        });
      });

      await c.setCognitoEmailOTPRequiredCb?.(() => {
        settle({
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "EMAIL_OTP",
        });
      });

      await c.setCognitoPinRequiredCb?.(() => {
        settle({
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "PIN_REQUIRED",
        });
      });

      await c.setCognitoCustomChallengeRequiredCb?.(() => {
        settle({
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "CUSTOM_CHALLENGE",
        });
      });

      try {
        const r = await awaitIfPromise<NativeCognitoLoginResult>(
          (c.cognitoUserLogin as (u: string, p: string) => unknown)(
            email,
            password
          )
        );

        if (settled) return;

        if (r?.challengePending) {
          settle({
            success: false,
            member: emptyFieldTeamMember(),
            token: "",
            challenge: "CUSTOM_CHALLENGE",
          });
          return;
        }

        if (r && typeof r === "object" && ("userId" in r || "email" in r)) {
          await finishWithTokens();
        }
      } catch {
        settle(failFieldTeamLogin());
      }
    })();
  });
}

export const CircadiaEngineProviderFactory: ICircadiaEngineProviderFactory = ({
  circadia,
}) => {
  let gColor1 = "";
  let gColor2 = "";

  const windowCircadiaStatus = !!circadia;
  const isWindowCircadia = () => {
    return windowCircadiaStatus;
  };
  const isIOS = !!circadia && circadia.plat === "ios";

  if (!circadia) {
    circadia = {
      saveConfig: async (key: string, value: string) => {
        return true;
      },
      createSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      changeSSPin: async (jwt: string, pin: string) => {
        return true;
      },
      saveSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        try {
          browserLocalStorage.set("saveSSvalue", JSON.stringify(storeValue));
          return true;
        } catch (error) {
          return Promise.reject();
        }
      },
      loadSSValue: async (email: string, key: string, value: string) => {
        const storeValue = {
          email,
          key,
          value,
        };
        const getSSvalue = JSON.parse(
          browserLocalStorage.get("saveSSvalue") || ""
        );
        return storeValue.value === getSSvalue.value ? true : Promise.reject();
      },
      cognitoProvidePin: async (pin: string) => {
        return true;
      },
      setStyleSheet: async (styes: string) => {
        return true;
      },
      setNotifTokenCb: (cb: (token: string) => void) => {
        const token = "dummy-token";
        cb(token);
        return undefined;
      },
      loadSSValueBio: async (email: string, key: string) => {
        return false;
      },
      loginIntercomUser: async (
        token: string,
        hmac: string,
        userInfo: {
          name: string;
          facility: string;
          user_id?: string;
          email?: string;
        }
      ) => {
        let response = null;
        if (circadia) {
          // TODO: remove then or await
          await circadia
            .loginIntercomUser(token, hmac, userInfo)
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      logoutIntercomUser: async () => {
        let response = null;
        if (circadia) {
          // TODO: remove then or await
          await circadia
            .logoutIntercomUser()
            .then((res: string) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      showIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .showIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      hideIntercom: async () => {
        let response = null;
        if (circadia) {
          await circadia
            .hideIntercom()
            .then((res: boolean) => {
              response = { code: "success", message: res };
            })
            .catch((err: { message: string }) => {
              response = { code: "error", message: err.message };
            });
        }
        return response;
      },
      cognitoProvideMfa: (mfa: string) => {},
      cognitoUserLogout: () => {},
      setCognitoLoginFailedCb: (
        cb: (reason: { Code: string; Msg: string }) => void
      ) => {},
      setCognitoLoginDoneCb: (cb: () => void) => {},
      setCognitoMfaRequiredCb: (
        cb: (reason: {
          CODE_DELIVERY_DELIVERY_MEDIUM: string;
          CODE_DELIVERY_DESTINATION: string;
        }) => void
      ) => {},
      setCognitoPinRequiredCb: (cb: () => void) => {},
      cognitoTokens: async () => {
        return { AccessToken: "", IdToken: "" };
      },
      playSound: async () => true,
      cognitoUserLogin: (username: string, password: string) => {},
      cognitoUserEmailOTPLogin: () => {},
      cognitoProvideEmailOTP: () => {},
      setCognitoEmailOTPRequiredCb: () => {},
      cognitoProvideNewPassword: (password: string, attrs?: unknown) => {},
      setCognitoNewPasswordRequiredCb: (cb: (attrs: unknown) => void) => {},
      plat: "web",
      cognitoChangePassword: (
        accessToken: string,
        prevPassword: string,
        newPassword: string
      ) => {
        return { mdg: "", code: 0 };
      },
      intfv: -1,
      sharePdf: async () => {},
      scanDeviceId: async () => {
        throw new Error("scanDeviceId is not available in the browser");
      },
      requestNotifPermission: async () => ({ granted: false }),
      scheduleLocalNotification: async () => {},
      cancelLocalNotification: async () => {},
      getPendingNotificationIds: async () => ({ ids: [] as number[] }),
      getCurrentSsid: async () => ({ ssid: null }),
      saveWifiCredentials: async () => {},
      getWifiCredentials: async () => ({ ssid: null, password: null }),
      clearWifiCredentials: async () => {},
      fieldTeamLogin: async () => ({
        success: false,
        member: {
          id: "",
          name: "",
          email: "",
          role: "field_technician" as const,
        },
        token: "",
      }),
      createPatientAccount: async () => ({
        success: false,
        patientId: "",
      }),
      scanWifiNetworks: async () => [],
      testWifiCredentials: async () => ({ success: false }),
      scanForCircadiaDevice: async () => null,
      provisionDeviceWifi: async () => ({ status: "timeout" as const }),
      openWifiSettings: async () => {},
      isDeviceReachable: async () => ({ reachable: false }),
      pingDevice: async () => ({ reachable: false }),
      checkServerConnection: async () => ({ connected: false }),
      checkMonitoringStatus: async () => ({ active: false }),
      waitForFirstReading: async () => ({ received: false }),
      completeSetup: async () => ({ success: false }),
      escalateSetup: async () => ({ ticketNumber: "" }),
      getTodaysInstalls: async () => [],
      getRecentInstalls: async () => [],
    };
  }

  const playSound = async (sound: string) => {
    if (isWindowCircadia()) {
      return circadia.playSound(sound);
    }
    return true;
  };

  const getDeviceToken = async () =>
    new Promise<string>((resolve) => {
      if (!circadia) {
        return resolve("");
      }

      circadia.setNotifTokenCb((token) => resolve(token));
      return "";
    });

  const changeAppTopAndBottom = (color1: string, color2: string) => {
    if (color1 === gColor1 && color2 === gColor2) return;
    gColor1 = color1;
    gColor2 = color2;

    if (!circadia) {
      return;
    }
    circadia.setStyleSheet(
      `background-color:qlineargradient(spread:pad, x1:0, y1:0, x2:0, y2:1, stop:0.000 ${color1},stop:0.250 ${color1} ,stop:0.750 ${color2}, stop:1.000 ${color2});`
    );
  };

  const loginIntercomUser = async (
    token: string,
    hmac: string,
    userInfo: {
      name: string;
      facility: string;
      user_id?: string;
      email?: string;
    }
  ) => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .loginIntercomUser(token, hmac, userInfo)
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const logoutIntercomUser = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .logoutIntercomUser()
        .then((res: string) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const showIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .showIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };
  const hideIntercom = async () => {
    let response = null;
    if (isWindowCircadia()) {
      await circadia
        .hideIntercom()
        .then((res: boolean) => {
          response = { code: "success", message: res };
        })
        .catch((err: { message: string }) => {
          response = { code: "error", message: err.message };
        });
    }
    return response;
  };

  const scanDeviceId = async (): Promise<{ deviceId: string }> => {
    return circadia.scanDeviceId();
  };

  const scheduleLocalNotification = (
    opts: Parameters<ICircadiaEngine["scheduleLocalNotification"]>[0]
  ): Promise<void> => {
    return circadia.scheduleLocalNotification(opts);
  };

  const cancelLocalNotification = (opts: { id: number }): Promise<void> => {
    return circadia.cancelLocalNotification(opts);
  };

  const getPendingNotificationIds = (): Promise<{ ids: number[] }> => {
    return circadia.getPendingNotificationIds();
  };

  const getCurrentSsid = (): Promise<{ ssid: string | null }> => {
    return circadia.getCurrentSsid();
  };

  const saveWifiCredentials = (options: {
    ssid: string;
    password: string;
  }): Promise<void> => {
    return circadia.saveWifiCredentials(options);
  };

  const getWifiCredentials = (): Promise<{
    ssid: string | null;
    password: string | null;
  }> => {
    return circadia.getWifiCredentials();
  };

  const clearWifiCredentials = (): Promise<void> => {
    return circadia.clearWifiCredentials();
  };

  const fieldTeamLogin = (email: string, password: string) => {
    if (!isWindowCircadia()) {
      return Promise.resolve(failFieldTeamLogin());
    }
    const bridge = circadia as ICircadiaEngine & {
      fieldTeamLogin?: (
        e: string,
        p: string
      ) => Promise<FieldTeamLoginResult>;
    };
    if (typeof bridge.fieldTeamLogin === "function") {
      return bridge.fieldTeamLogin(email, password);
    }
    return fieldTeamLoginViaNativeCognitoEvents(circadia, email, password);
  };

  const fieldTeamSubmitNewPassword = async (
    profileEmail: string,
    newPassword: string,
    userAttributes?: Record<string, string>
  ): Promise<FieldTeamLoginResult> => {
    if (!isWindowCircadia()) {
      return failFieldTeamLogin();
    }
    const c = circadia as ICircadiaEngine & {
      cognitoProvideNewPassword?: (
        password: string,
        attrs?: Record<string, string>
      ) => void | Promise<NativeCognitoLoginResult>;
      cognitoTokens?: () => Promise<{ AccessToken?: string; IdToken?: string }>;
    };
    if (typeof c.cognitoProvideNewPassword !== "function") {
      return failFieldTeamLogin();
    }
    try {
      const r = await awaitIfPromise<NativeCognitoLoginResult>(
        (c.cognitoProvideNewPassword as (
          p: string,
          a?: Record<string, string>
        ) => unknown)(newPassword, userAttributes)
      );
      if (r?.challengePending) {
        return {
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "CUSTOM_CHALLENGE",
        };
      }
      return finalizeNativeFieldTeamLogin(c, profileEmail);
    } catch {
      return failFieldTeamLogin();
    }
  };

  const fieldTeamSubmitMfa = async (
    profileEmail: string,
    code: string
  ): Promise<FieldTeamLoginResult> => {
    if (!isWindowCircadia()) {
      return failFieldTeamLogin();
    }
    const c = circadia as ICircadiaEngine & {
      cognitoProvideMfa?: (
        mfa: string
      ) => void | Promise<NativeCognitoLoginResult>;
      cognitoTokens?: () => Promise<{ AccessToken?: string; IdToken?: string }>;
    };
    if (typeof c.cognitoProvideMfa !== "function") {
      return failFieldTeamLogin();
    }
    try {
      const r = await awaitIfPromise<NativeCognitoLoginResult>(
        (c.cognitoProvideMfa as (m: string) => unknown)(code)
      );
      if (r?.challengePending) {
        return {
          success: false,
          member: emptyFieldTeamMember(),
          token: "",
          challenge: "CUSTOM_CHALLENGE",
        };
      }
      return finalizeNativeFieldTeamLogin(c, profileEmail);
    } catch {
      return failFieldTeamLogin();
    }
  };

  const createPatientAccount = (
    params: Parameters<ICircadiaEngine["createPatientAccount"]>[0]
  ) => {
    if (isWindowCircadia()) {
      return circadia.createPatientAccount(params);
    }
    return Promise.resolve({ success: false, patientId: "" });
  };

  const scanWifiNetworks = async (): Promise<WifiScanRow[]> => {
    if (!isWindowCircadia()) {
      return [];
    }
    const engine = circadia as ICircadiaEngine;
    try {
      if (typeof engine.scanDeviceWifiNetworks === "function") {
        const raw = await engine.scanDeviceWifiNetworks();
        return normalizeWifiScanResult(raw);
      }
      if (typeof engine.scanWifiNetworks === "function") {
        const raw = await engine.scanWifiNetworks();
        return normalizeWifiScanResult(raw);
      }
    } catch {
      /* fall through */
    }
    return [];
  };

  const testWifiCredentials = (ssid: string, password: string) => {
    if (isWindowCircadia()) {
      return circadia.testWifiCredentials(ssid, password);
    }
    return Promise.resolve({ success: false });
  };

  const isDeviceReachable = () => {
    if (
      isWindowCircadia() &&
      typeof (circadia as ICircadiaEngine).isDeviceReachable === "function"
    ) {
      return (circadia as ICircadiaEngine).isDeviceReachable();
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ reachable: true });
    }
    return Promise.resolve({ reachable: false });
  };

  const pingDevice = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.pingDevice(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ reachable: true });
    }
    return Promise.resolve({ reachable: false });
  };

  const checkServerConnection = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.checkServerConnection(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ connected: true });
    }
    return Promise.resolve({ connected: false });
  };

  const checkMonitoringStatus = (deviceId: string) => {
    if (isWindowCircadia()) {
      return circadia.checkMonitoringStatus(deviceId);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ active: true });
    }
    return Promise.resolve({ active: false });
  };

  const waitForFirstReading = (deviceId: string, timeoutMs: number) => {
    if (isWindowCircadia()) {
      return circadia.waitForFirstReading(deviceId, timeoutMs);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ received: true });
    }
    return Promise.resolve({ received: false });
  };

  const completeSetup = (
    params: Parameters<ICircadiaEngine["completeSetup"]>[0]
  ) => {
    if (isWindowCircadia()) {
      const fn = (
        circadia as ICircadiaEngine & {
          completeSetup?: (
            p: Parameters<ICircadiaEngine["completeSetup"]>[0]
          ) => ReturnType<ICircadiaEngine["completeSetup"]>;
        }
      ).completeSetup;
      if (typeof fn === "function") {
        return fn.call(circadia, params);
      }
      /* Native bridge often has no `completeSetup`; treat as OK so the web flow can finish */
      return Promise.resolve({ success: true });
    }
    return Promise.resolve({ success: false });
  };

  const escalateSetup = (
    params: Parameters<ICircadiaEngine["escalateSetup"]>[0]
  ) => {
    if (isWindowCircadia()) {
      return circadia.escalateSetup(params);
    }
    return Promise.resolve({ ticketNumber: "" });
  };

  const getTodaysInstalls = () => {
    if (isWindowCircadia()) {
      return circadia.getTodaysInstalls();
    }
    return Promise.resolve([]);
  };

  const getRecentInstalls = (limit: number) => {
    if (isWindowCircadia()) {
      return circadia.getRecentInstalls(limit);
    }
    return Promise.resolve([]);
  };

  const scanForCircadiaDevice = () => {
    if (isWindowCircadia()) {
      return circadia.scanForCircadiaDevice();
    }
    if (import.meta.env.DEV) {
      return new Promise<{
        deviceId: string;
        deviceName: string;
      } | null>((resolve) => {
        setTimeout(
          () =>
            resolve({
              deviceId: "dev-circadia-device",
              deviceName: "Circadia Sensor",
            }),
          2500
        );
      });
    }
    return new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 30_000);
    });
  };

  const provisionDeviceWifi = (
    params: Parameters<ICircadiaEngine["provisionDeviceWifi"]>[0]
  ) => {
    if (isWindowCircadia()) {
      const fn = (
        circadia as ICircadiaEngine & {
          provisionDeviceWifi?: (...args: unknown[]) => Promise<unknown>;
        }
      ).provisionDeviceWifi;
      if (typeof fn === "function") {
        const auth = params.auth ?? params.securityType;
        const payload = {
          deviceId: params.deviceId,
          ssid: params.ssid,
          password: params.password ?? "",
          securityType: params.securityType,
          auth,
        };
        return Promise.resolve(fn.call(circadia, payload))
          .then(mapNativeProvisionResult)
          .catch(() => ({ status: "timeout" as const }));
      }
      return circadia.provisionDeviceWifi(params);
    }
    if (import.meta.env.DEV) {
      return Promise.resolve({ status: "connected" as const });
    }
    return Promise.resolve({ status: "timeout" as const });
  };

  const openWifiSettings = () => {
    if (
      isWindowCircadia() &&
      typeof (circadia as ICircadiaEngine).openWifiSettings === "function"
    ) {
      return (circadia as ICircadiaEngine).openWifiSettings();
    }
    return Promise.resolve();
  };

  /**
   * Invoke `window.circadiaapp.getAppVersion()` lazily so Android's late-
   * arriving bridge is honored (vs. a snapshot at factory init time).
   * Validates the payload shape (`{ versionName, versionCode }`) and
   * returns undefined for the web fallback, when the method isn't shipped
   * yet, when the call throws, or when the payload is malformed.
   */
  const getNativeAppVersion = async (): Promise<
    { versionName: string; versionCode: string | number } | undefined
  > => {
    if (!isWindowCircadia()) return undefined;
    const fn = (circadia as { getAppVersion?: () => unknown }).getAppVersion;
    if (typeof fn !== "function") return undefined;
    try {
      const raw = (await fn()) as
        | { versionName?: unknown; versionCode?: unknown }
        | null
        | undefined;
      if (!raw || typeof raw !== "object") return undefined;
      const versionName =
        typeof raw.versionName === "string" && raw.versionName.trim()
          ? raw.versionName.trim()
          : undefined;
      const versionCode =
        typeof raw.versionCode === "string" ||
        typeof raw.versionCode === "number"
          ? raw.versionCode
          : undefined;
      if (!versionName || versionCode === undefined) return undefined;
      return { versionName, versionCode };
    } catch {
      return undefined;
    }
  };

  return {
    ...circadia,
    changeAppTopAndBottom,
    getDeviceToken,
    hideIntercom,
    showIntercom,
    logoutIntercomUser,
    loginIntercomUser,
    isWindowCircadia,
    playSound,
    scanDeviceId,
    scheduleLocalNotification,
    cancelLocalNotification,
    getPendingNotificationIds,
    getCurrentSsid,
    saveWifiCredentials,
    getWifiCredentials,
    clearWifiCredentials,
    fieldTeamLogin,
    fieldTeamSubmitNewPassword,
    fieldTeamSubmitMfa,
    createPatientAccount,
    scanWifiNetworks,
    testWifiCredentials,
    scanForCircadiaDevice,
    provisionDeviceWifi,
    openWifiSettings,
    isDeviceReachable,
    pingDevice,
    checkServerConnection,
    checkMonitoringStatus,
    waitForFirstReading,
    completeSetup,
    escalateSetup,
    getTodaysInstalls,
    getRecentInstalls,
    isIOS,
    getNativeAppVersion,
  };
};
